import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { LIST_LIMIT_MAX } from '../../../core/constants/network';
import { normalizeEvent } from '../../../common/package/activity';
import { normalizeList } from '../../../common/package/list';
import { normalizeReviewRound } from '../../../common/package/review-round';
import { normalizeTask } from '../../../common/package/task';
import { eventKeys, taskKeys } from './keys';
import { eventPaths, taskPaths } from './paths';

function compact(params) {
    return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''));
}

// Tasks are read by home, projects, work and reviews, so they live here per
// vite/03 "2+ features read it". Every list sends modality=all because Core
// otherwise hides one-off tasks.
export function useTasksList({ projectId, tierId, workflowState, kind, limit = LIST_LIMIT_MAX, offset = 0 } = {}) {
    const params = compact({
        project_id: projectId,
        tier_id: tierId,
        workflow_state: workflowState,
        kind,
        modality: 'all',
        limit,
        offset,
    });
    return useQuery({
        queryKey: taskKeys.list(params),
        queryFn: async ({ signal }) => normalizeList((await api.get(taskPaths.list(), { params, signal })).data, normalizeTask),
        placeholderData: keepPreviousData,
    });
}

export function useTask(id) {
    return useQuery({
        queryKey: taskKeys.detail(id),
        queryFn: async ({ signal }) => normalizeTask((await api.get(taskPaths.detail(id), { signal })).data),
        enabled: id != null,
    });
}

export function useTaskReviewRounds(id) {
    return useQuery({
        queryKey: taskKeys.reviewRounds(id),
        queryFn: async ({ signal }) => normalizeList(
            (await api.get(taskPaths.reviewRounds(id), { params: { limit: 25 }, signal })).data,
            normalizeReviewRound,
        ),
        enabled: id != null,
    });
}

export function useEvents({ projectId, taskId, limit = 20 } = {}) {
    const params = compact({ project_id: projectId, task_id: taskId, limit });
    return useQuery({
        queryKey: eventKeys.list(params),
        queryFn: async ({ signal }) => normalizeList((await api.get(eventPaths.list(), { params, signal })).data, normalizeEvent),
    });
}
